import type { Concept } from '@yuan-math/curriculum';

/**
 * Precalculus — the Grade 12 year, and the one where the answer stops being a
 * number you can finish writing down.
 *
 * Every band before this one ends in a value. Algebra I ends in x = 4, Geometry
 * in a proved equality, Algebra II in a curve you can draw. This course keeps
 * ending in a *process* instead: a sum with infinitely many terms that
 * nevertheless has a total, a count far too large to make by listing, a height
 * a curve approaches and never takes.
 *
 * The through-line is the limit, and the course is arranged so a student meets
 * it three times before it is named. They meet it in `series`, where
 * 1/2 + 1/4 + 1/8 + … is agreed to be exactly 1 despite never arriving. They
 * met it already in Algebra II, at the asymptote of a rational function. Only
 * in `limits`, at the end, is it given a name and a notation — by which point
 * the idea is old and only the notation is new. That ordering is the design of
 * the course, and it is pinned by a test.
 *
 * Three orderings deserve their comments.
 *
 * `limits` depends on `series`, for the reason above: the infinite geometric
 * sum is the student's first limit, computed before they have the word for it.
 *
 * `binomial-theorem` depends on `combinatorics-intro`. The numbers in Pascal's
 * triangle are not a pattern that happens to work; each one counts the ways of
 * choosing which brackets contribute a y. Taught in the other order the
 * triangle is a magic trick, and a student who has only seen the trick cannot
 * expand (x + y)²⁰ or say why each row sums to a power of two.
 *
 * `law-of-sines-cosines` depends on `unit-circle`, and not merely on
 * `trig-ratios`. An oblique triangle has an obtuse angle roughly half the time,
 * and sin 130° is not defined by any right triangle. A student sent into these
 * two laws with only the triangle definition hits a wall at the first ambiguous
 * case with nothing to think with.
 *
 * Four later additions extend the same argument rather than adding topics
 * beside it.
 *
 * `trig-addition-formulas` pays off a warning `trig-identities` already gives:
 * that sin(A + B) is not sin A + sin B, and that the real expansion exists. A
 * course that raises that objection and never answers it teaches a student to
 * distrust the one rule they were told, without giving them the one they need.
 *
 * `mathematical-induction` sits on `series` because that is where the student
 * first met a formula they could check but not justify. Gauss's pairing gives
 * n(n + 1)/2 for a *particular* n; induction is how a claim about every n at
 * once gets settled, which is the same trade the limit makes — infinitely many
 * cases, handled in finitely many lines.
 *
 * `polar-coordinates` follows `vectors` rather than `coordinate-plane`,
 * because magnitude and direction is exactly what a vector already is: polar
 * form is that pair used as an address rather than as an arrow. It also
 * repeats the lesson of `trig-equations` in a second setting — one point has
 * infinitely many names, and the work is knowing which ones.
 *
 * `rates-of-change` closes the year. It is the first question whose answer
 * *needs* the limit rather than merely tolerating it: the rate at an instant is
 * a quotient whose denominator has to be nothing, and no arithmetic gets there.
 *
 * Three more finish arguments the course was starting and abandoning.
 *
 * `half-angle-formulas` follows `trig-addition-formulas` because both of its
 * halves are that lesson read in reverse. tan(A + B) is the sine expansion over
 * the cosine expansion; the half-angle formulas are cos 2θ = 1 − 2sin²θ solved
 * for the sine rather than for the double angle. A course that gives a student
 * cos 2θ and never asks them to run it backwards has taught a formula rather
 * than a technique — and left them unable to integrate sin²x, which is the
 * first place the half-angle form is not optional.
 *
 * `dot-product` sits on `trig-identities` as well as on `vectors`, because the
 * proof that a·b = |a||b|cos θ is the cosine rule applied to the triangle the
 * two vectors make. Without that identity the formula is a second, unrelated
 * definition of multiplication that happens to detect right angles.
 *
 * `complex-polar` needs `polar-coordinates` and not merely `complex-numbers`.
 * Algebra II drew the plane and stopped; the point of the plane is that
 * multiplication by a complex number adds the arguments, and "adds the
 * arguments" is unsayable without an argument. This is also the one place in
 * school mathematics where the answer to "what does i *do*" is a sentence
 * rather than a rule: it turns the plane a quarter turn.
 */
export const PRECALCULUS_CONCEPTS: Concept[] = [
  /* ------------------------------------------------ trigonometry, finished */
  {
    id: 'trig-identities',
    title: 'Trigonometric Identities',
    summary: 'Equations that hold for every angle at once, and what they are for.',
    strand: 'trigonometry',
    levels: ['grade-12'],
    prerequisites: ['unit-circle', 'trig-graphs'],
    status: 'available',
    estimatedMinutes: 40,
  },
  {
    id: 'trig-addition-formulas',
    title: 'Sum, Difference and Double Angle',
    summary: 'What sin(A + B) really is, once you accept that it is not sin A + sin B.',
    strand: 'trigonometry',
    levels: ['grade-12'],
    prerequisites: ['trig-identities'],
    status: 'available',
    estimatedMinutes: 40,
  },
  {
    id: 'half-angle-formulas',
    title: 'Tangent and Half Angle',
    summary: 'The two formulas the addition lesson left out, and the one that runs a doubling backwards.',
    strand: 'trigonometry',
    levels: ['grade-12'],
    prerequisites: ['trig-addition-formulas'],
    status: 'available',
    estimatedMinutes: 40,
  },
  {
    id: 'trig-equations',
    title: 'Trigonometric Equations',
    summary: 'Equations whose answer is not a number but a pattern of numbers, repeating forever.',
    strand: 'trigonometry',
    levels: ['grade-12'],
    prerequisites: ['trig-identities', 'unit-circle'],
    status: 'available',
    estimatedMinutes: 40,
  },
  {
    id: 'law-of-sines-cosines',
    title: 'The Sine and Cosine Rules',
    summary: 'Solving a triangle with no right angle — and noticing when two different triangles fit the same measurements.',
    strand: 'trigonometry',
    levels: ['grade-12'],
    prerequisites: ['trig-ratios', 'unit-circle'],
    status: 'available',
    estimatedMinutes: 40,
  },

  /* --------------------------------------------------- sums that never end */
  {
    id: 'series',
    title: 'Series and Summation',
    summary: 'What happens when a sequence is added up, including one that is never finished.',
    strand: 'discrete',
    levels: ['grade-12'],
    prerequisites: ['sequences'],
    status: 'available',
    estimatedMinutes: 40,
  },

  {
    id: 'mathematical-induction',
    title: 'Proof by Induction',
    summary: 'Settling infinitely many claims at once, with an argument that fits on a page.',
    strand: 'discrete',
    levels: ['grade-12'],
    prerequisites: ['series'],
    status: 'available',
    estimatedMinutes: 40,
  },

  /* ----------------------------------------------- counting without listing */
  {
    id: 'combinatorics-intro',
    title: 'Counting',
    summary: 'Answering "how many ways?" without writing any of them down.',
    strand: 'discrete',
    levels: ['grade-12'],
    prerequisites: ['basic-probability'],
    status: 'available',
    estimatedMinutes: 40,
  },
  {
    id: 'binomial-theorem',
    title: 'The Binomial Theorem',
    summary: 'Expanding a bracket to the twentieth power by counting, rather than by multiplying twenty times.',
    strand: 'algebra',
    levels: ['grade-12'],
    prerequisites: ['combinatorics-intro', 'polynomials'],
    status: 'available',
    estimatedMinutes: 35,
  },
  {
    id: 'probability-distributions',
    title: 'Probability Distributions',
    summary: 'Describing a random quantity by the chance of every outcome it has.',
    strand: 'probability',
    levels: ['grade-12'],
    prerequisites: ['combinatorics-intro', 'mean-median-mode'],
    status: 'available',
    estimatedMinutes: 40,
  },

  /* ---------------------------------------------- geometry with an equation */
  {
    id: 'conic-sections',
    title: 'Conic Sections',
    summary: 'Four curves that are one curve, cut from a cone at four different angles.',
    strand: 'geometry',
    levels: ['grade-12'],
    prerequisites: ['quadratic-graphs', 'coordinate-geometry'],
    status: 'available',
    estimatedMinutes: 40,
  },
  {
    id: 'vectors',
    title: 'Vectors',
    summary: 'Quantities that carry a direction as well as a size, and add nose to tail rather than digit to digit.',
    strand: 'linear-algebra',
    levels: ['grade-12'],
    prerequisites: ['coordinate-plane', 'trig-ratios'],
    status: 'available',
    estimatedMinutes: 40,
  },

  {
    id: 'dot-product',
    title: 'The Dot Product',
    summary: 'Multiplying two vectors and getting a plain number — which turns out to measure the angle between them.',
    strand: 'linear-algebra',
    levels: ['grade-12'],
    prerequisites: ['vectors', 'trig-identities'],
    status: 'available',
    estimatedMinutes: 40,
  },

  {
    id: 'polar-coordinates',
    title: 'Polar Coordinates',
    summary: 'Giving a point an address by direction and distance, rather than by across and up.',
    strand: 'geometry',
    levels: ['grade-12'],
    prerequisites: ['vectors', 'unit-circle'],
    status: 'available',
    estimatedMinutes: 40,
  },

  {
    id: 'complex-polar',
    title: 'The Complex Plane',
    summary: 'Giving a complex number an address — and finding that multiplying by one is a rotation.',
    strand: 'numbers',
    levels: ['grade-12'],
    prerequisites: ['complex-numbers', 'polar-coordinates'],
    status: 'available',
    estimatedMinutes: 40,
  },

  /* ------------------------------------------------------ the idea, named */
  {
    id: 'limits',
    title: 'Limits',
    summary: 'The value a function heads towards — whether or not it ever gets there.',
    strand: 'calculus',
    levels: ['grade-12'],
    prerequisites: ['rational-functions', 'series'],
    status: 'available',
    estimatedMinutes: 40,
  },
  {
    id: 'rates-of-change',
    title: 'Rates of Change',
    summary: 'How fast something is changing over an interval, and then at a single instant.',
    strand: 'calculus',
    levels: ['grade-12'],
    prerequisites: ['limits', 'linear-functions'],
    status: 'available',
    estimatedMinutes: 40,
  },
];
